import { useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Booking } from '@coachapp/shared';

const HOUR_HEIGHT = 56;
const DEFAULT_WORKING_HOURS = { start: '09:00', end: '21:00' };

export interface WeekCalendarProps {
  bookings: Booking[];
  workingHours?: { start: string; end: string };
  weekStart: Date;
  onWeekChange: (weekStart: Date) => void;
  onBookingClick?: (booking: Booking) => void;
  onSlotClick?: (day: Date, hour: number) => void;
  onDayClick?: (day: Date) => void;
}

function parseTimeToMinutes(time: string): number {
  const [hourPart, minutePart = '0'] = time.split(':');
  return Number(hourPart) * 60 + Number(minutePart);
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function getWeekBookings(bookings: Booking[], weekDays: Date[]): Booking[] {
  return bookings.filter((booking) => {
    const bookingDay = new Date(booking.startsAt);
    return weekDays.some((day) => isSameDay(bookingDay, day));
  });
}

function getHourRange(start: number, end: number): number[] {
  const hours: number[] = [];
  for (let h = start; h < end; h++) {
    hours.push(h);
  }
  return hours;
}

function getBookingTimeRange(
  booking: Booking,
): { startMinutes: number; endMinutes: number } {
  const bookingStart = new Date(booking.startsAt);
  const startMinutes = bookingStart.getHours() * 60 + bookingStart.getMinutes();
  return {
    startMinutes,
    endMinutes: startMinutes + booking.durationMinutes,
  };
}

function computeVisibleHourRange(
  workingHours: { start: string; end: string },
  bookings: Booking[],
  weekDays: Date[],
): { startHour: number; endHour: number } {
  const workStartMinutes = parseTimeToMinutes(workingHours.start);
  const workEndMinutes = parseTimeToMinutes(workingHours.end);

  let rangeStartMinutes = workStartMinutes;
  let rangeEndMinutes = workEndMinutes;

  for (const booking of getWeekBookings(bookings, weekDays)) {
    const { startMinutes, endMinutes } = getBookingTimeRange(booking);
    rangeStartMinutes = Math.min(rangeStartMinutes, startMinutes);
    rangeEndMinutes = Math.max(rangeEndMinutes, endMinutes);
  }

  const startHour = Math.max(0, Math.floor(rangeStartMinutes / 60));
  let endHour = Math.min(24, Math.ceil(rangeEndMinutes / 60));

  if (endHour <= startHour) {
    endHour = Math.min(24, startHour + 1);
  }

  return { startHour, endHour };
}

function getWeekDays(weekStart: Date): Date[] {
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(weekStart);
    day.setDate(day.getDate() + i);
    days.push(day);
  }
  return days;
}

function getStatusColors(status: string): string {
  switch (status) {
    case 'CONFIRMED':
      return 'bg-primary text-white border-primary-dark/30';
    case 'COMPLETED':
      return 'bg-blue-500 text-white border-blue-600/30';
    case 'CANCELLED':
      return 'bg-red-400/80 text-white border-red-500/30 line-through opacity-75';
    case 'PENDING':
      return 'bg-amber-400 text-white border-amber-500/30';
    default:
      return 'bg-primary/80 text-white border-primary-dark/20';
  }
}

function getBookingPosition(
  booking: Booking,
  day: Date,
  startHour: number,
  endHour: number,
  totalHeight: number,
): { top: number; height: number } | null {
  const bookingStart = new Date(booking.startsAt);
  if (!isSameDay(bookingStart, day)) return null;

  const { startMinutes, endMinutes } = getBookingTimeRange(booking);
  const rangeStart = startHour * 60;
  const rangeEnd = endHour * 60;

  const visibleStart = Math.max(startMinutes, rangeStart);
  const visibleEnd = Math.min(endMinutes, rangeEnd);
  if (visibleStart >= visibleEnd) return null;

  const top = ((visibleStart - rangeStart) / 60) * HOUR_HEIGHT;
  const availableHeight = totalHeight - top;
  if (availableHeight <= 0) return null;

  const rawHeight = ((visibleEnd - visibleStart) / 60) * HOUR_HEIGHT;
  const height = Math.min(Math.max(rawHeight, 28), availableHeight);
  if (height < 1) return null;

  return { top, height };
}

const WeekCalendar = ({
  bookings,
  workingHours,
  weekStart,
  onWeekChange,
  onBookingClick,
  onSlotClick,
  onDayClick,
}: WeekCalendarProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const weekDays = useMemo(() => getWeekDays(weekStart), [weekStart]);
  const today = new Date();
  const todayIndex = weekDays.findIndex((day) => isSameDay(day, today));

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollToToday = () => {
      if (todayIndex < 0) {
        container.scrollLeft = 0;
        return;
      }

      if (container.scrollWidth <= container.clientWidth) return;

      const todayColumn = container.querySelector<HTMLElement>(
        `[data-day-index="${todayIndex}"]`,
      );
      if (!todayColumn) return;

      const containerRect = container.getBoundingClientRect();
      const columnRect = todayColumn.getBoundingClientRect();
      const columnCenter =
        columnRect.left - containerRect.left + container.scrollLeft + columnRect.width / 2;
      const targetScroll = columnCenter - container.clientWidth / 2;

      container.scrollLeft = Math.max(
        0,
        Math.min(targetScroll, container.scrollWidth - container.clientWidth),
      );
    };

    scrollToToday();

    const resizeObserver = new ResizeObserver(scrollToToday);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [weekStart, todayIndex]);

  const { startHour, endHour, hours, totalHeight } = useMemo(() => {
    const wh = workingHours ?? DEFAULT_WORKING_HOURS;
    const range = computeVisibleHourRange(wh, bookings, weekDays);
    const hourList = getHourRange(range.startHour, range.endHour);
    return {
      startHour: range.startHour,
      endHour: range.endHour,
      hours: hourList,
      totalHeight: hourList.length * HOUR_HEIGHT,
    };
  }, [workingHours, bookings, weekDays]);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const weekLabel = `${weekStart.toLocaleDateString(locale, { day: 'numeric', month: 'short' })} – ${weekEnd.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}`;

  const goToPrevWeek = () => {
    const prev = new Date(weekStart);
    prev.setDate(prev.getDate() - 7);
    onWeekChange(prev);
  };

  const goToNextWeek = () => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + 7);
    onWeekChange(next);
  };

  const formatHour = (hour: number) =>
    `${hour.toString().padStart(2, '0')}:00`;

  return (
    <div className="card p-0 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <button
          type="button"
          onClick={goToPrevWeek}
          className="btn-ghost"
          aria-label={t('Предыдущая неделя')}
        >
          <ChevronLeft size={20} />
        </button>
        <span className="text-sm font-bold text-slate-900">{weekLabel}</span>
        <button
          type="button"
          onClick={goToNextWeek}
          className="btn-ghost"
          aria-label={t('Следующая неделя')}
        >
          <ChevronRight size={20} />
        </button>
      </div>

      <div ref={scrollContainerRef} className="overflow-x-auto">
        <div className="min-w-[640px]">
          <div className="flex border-b border-slate-100 bg-slate-50/50">
            <div className="sticky left-0 z-30 w-14 shrink-0 bg-slate-50 border-r border-slate-100" />
            {weekDays.map((day, dayIndex) => {
              const isToday = isSameDay(day, today);
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  data-day-index={dayIndex}
                  onClick={() => onDayClick?.(day)}
                  aria-label={day.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}
                  className={`flex-1 min-w-[88px] py-2 text-center border-l border-slate-100 ${isToday ? 'bg-primary-soft' : ''}`}
                >
                  <div className={`micro-label ${isToday ? 'text-primary' : 'text-slate-400'}`}>
                    {day.toLocaleDateString(locale, { weekday: 'short' })}
                  </div>
                  <div className={`text-sm font-bold ${isToday ? 'text-primary' : 'text-slate-900'}`}>
                    {day.getDate()}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex">
            <div className="sticky left-0 z-20 w-14 shrink-0 bg-white border-r border-slate-100 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.08)]">
              {hours.map((hour) => (
                <div
                  key={hour}
                  style={{ height: HOUR_HEIGHT }}
                  className="flex items-start justify-end pr-2 pt-1 text-[11px] font-semibold text-slate-400 tabular-nums"
                >
                  {formatHour(hour)}
                </div>
              ))}
            </div>

            <div className="flex flex-1">
              {weekDays.map((day) => {
                const isToday = isSameDay(day, today);
                const dayBookings = bookings.filter((b) =>
                  isSameDay(new Date(b.startsAt), day),
                );

                return (
                  <div
                    key={day.toISOString()}
                    className={`flex-1 min-w-[88px] border-l border-slate-100 relative ${isToday ? 'bg-primary/[0.02]' : ''}`}
                    style={{ height: totalHeight }}
                  >
                    {hours.map((hour, i) => (
                      <button
                        key={hour}
                        type="button"
                        onClick={() => onSlotClick?.(day, hour)}
                        className="absolute w-full border-b border-slate-50 hover:bg-primary/5 transition-colors cursor-pointer"
                        style={{ top: i * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                        aria-label={t('Создать тренировку в {{time}}', {
                          time: formatHour(hour),
                        })}
                      />
                    ))}

                    {dayBookings.map((booking) => {
                      const pos = getBookingPosition(booking, day, startHour, endHour, totalHeight);
                      if (!pos) return null;

                      return (
                        <button
                          key={booking.id}
                          type="button"
                          onClick={() => onBookingClick?.(booking)}
                          className={`absolute left-1 right-1 rounded-xl border px-2 py-1 text-left text-[11px] font-bold shadow-sm overflow-hidden transition-opacity hover:opacity-90 z-[1] ${getStatusColors(booking.status)}`}
                          style={{ top: pos.top, height: pos.height }}
                        >
                          <span className="block truncate">
                            {booking.client?.name || t('Клиент')}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeekCalendar;
